import Papa from 'papaparse';
import { useEffect, useMemo, useState } from 'react';
import { useGlobalToasts } from '../../../hooks/Toast/useGlobalToasts';
import { WarningSeverity } from '../../../types/WarningSeverity';
import { EMPTY_ARRAY } from '../../../utils';
import { BlotterTable, useBlotterObservables, useBlotterTable, type ColumnDef } from '../../BlotterTable';
import type { WarningColumnValue } from '../../BlotterTable/columns/warning.types';
import { Button } from '../../Button';
import { Box } from '../../Core';
import { Dialog } from '../../Dialog';
import { FileArea } from '../../FileArea';
import { FormControlSizes } from '../../Form/types';
import { IconName } from '../../Icons';
import { NotificationVariants } from '../../Notification';
import {
  getEntityAdminBulkUploadFields,
  getEntityAdminBulkUploadRows,
  type EntityAdminBulkUploadParams,
  type EntityAdminBulkUploadRow,
} from '../getEntityAdminBulkUploadRows';
import type { EntityAdminRecord } from '../types';

type UploadRow = Pick<
  EntityAdminBulkUploadRow<EntityAdminRecord, EntityAdminRecord>,
  'rowID' | 'action' | 'warning' | 'values'
>;
type PreviewRow = UploadRow & {
  warningValue: Pick<WarningColumnValue, 'severity'> | undefined;
  warningSeverity: WarningSeverity | undefined;
};

export interface EntityAdminBulkUploadDialogProps<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
> {
  isOpen: boolean;
  close: () => void;
  entityName?: string;
  getBulkUploadParams: () => EntityAdminBulkUploadParams<TRecord, TDrawerRecord>;
  onImport: (rows: EntityAdminBulkUploadRow<TRecord, TDrawerRecord>[]) => Promise<void>;
}

export function EntityAdminBulkUploadDialog<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
>({
  isOpen,
  close,
  entityName = 'Entity',
  getBulkUploadParams,
  onImport,
}: EntityAdminBulkUploadDialogProps<TRecord, TDrawerRecord>) {
  const { add: addToast } = useGlobalToasts();
  const [rows, setRows] = useState<EntityAdminBulkUploadRow<TRecord, TDrawerRecord>[]>();
  const fields = useMemo(
    () => (isOpen ? getEntityAdminBulkUploadFields(getBulkUploadParams()) : EMPTY_ARRAY),
    [isOpen, getBulkUploadParams]
  );
  const validRows = useMemo(() => rows?.filter(row => row.warning == null) ?? EMPTY_ARRAY, [rows]);

  useEffect(() => {
    if (isOpen) {
      setRows(undefined);
    }
  }, [isOpen]);

  const handleUpload = (file: File | undefined) => {
    if (file == null) {
      return;
    }
    Papa.parse<Record<string, string>>(file, {
      delimiter: ',',
      header: true,
      skipEmptyLines: true,
      complete: result => {
        const error = result.errors.at(0)?.message ?? (result.data.length === 0 ? 'No rows found' : undefined);
        if (error) {
          addToast({ text: error, variant: NotificationVariants.Negative });
          return;
        }
        setRows(getEntityAdminBulkUploadRows(result.data, getBulkUploadParams()));
      },
    });
  };

  return (
    <Dialog
      isOpen={isOpen}
      close={close}
      title={`Upload ${entityName}`}
      width={960}
      showClose
      alignContent="left"
      dataTestId="entity-admin-bulk-upload-dialog"
      headerSuffix={
        <Button
          forwardedAs="a"
          href={`data:text/csv;charset=utf-8,${encodeURIComponent(`${fields.join(',')}\n`)}`}
          download={`${entityName} Template.csv`}
          size={FormControlSizes.Small}
          startIcon={IconName.Download}
          data-testid="entity-admin-bulk-upload-template"
        >
          Download Template
        </Button>
      }
      confirmLabel={`Import ${validRows.length} row${validRows.length === 1 ? '' : 's'}`}
      confirmDisabled={validRows.length === 0}
      onConfirm={() => {
        // Close right away, bulkEditQueue toasts report the progress
        onImport(validRows);
      }}
    >
      <Box h="400px" w="100%">
        {rows == null ? (
          <FileArea onChange={handleUpload} accept="text/csv" />
        ) : (
          <Box mx="-spacing16" h="100%">
            <EntityAdminBulkUploadPreview rows={rows} fields={fields} />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

function EntityAdminBulkUploadPreview({ rows, fields }: { rows: UploadRow[]; fields: string[] }) {
  const previewRows = useMemo(
    () =>
      rows.map(
        // The warning column spreads `field` as `{ severity }`. Row highlighting
        // compares `warningSeverity === HIGH`.
        (row): PreviewRow => ({
          ...row,
          warningValue: row.warning ? { severity: WarningSeverity.HIGH } : undefined,
          warningSeverity: row.warning ? WarningSeverity.HIGH : undefined,
        })
      ),
    [rows]
  );
  const columns = useMemo(
    (): ColumnDef<PreviewRow>[] => [
      { type: 'warning', field: 'warningValue', tooltipField: 'warning' },
      { type: 'text', field: 'action', title: 'Action', width: 90 },
      ...fields.map(
        (field): ColumnDef<PreviewRow> => ({ type: 'text', id: field, field: `values.${field}`, title: field })
      ),
    ],
    [fields]
  );
  const { dataObservable, errorObservable } = useBlotterObservables({ data: previewRows });
  const blotterTable = useBlotterTable<PreviewRow>({ dataObservable, errorObservable, columns, rowID: 'rowID' });

  return <BlotterTable {...blotterTable} background="backgroundModal" />;
}
